import type { SmartTruncateEnum } from '@talos/kyoko';
import { Box, HStack, Icon, IconName, InlineFormattedNumber, Text, VStack } from '@talos/kyoko';
import { Sparkline } from '@talos/kyoko/src/components/Watchlist/Sparkline';
import type { ICellRendererParams } from 'ag-grid-community';
import { isFlatAtIncrement, numberSignVariant } from '../core';
import type { AssetLeaderboardEntity } from './AssetLeaderboardEntity';
import { ASSET_LEADERBOARD_PERCENT_DISPLAY_INCREMENT } from './tokens';

/** Shown when a cell has no value, so "no data" reads as intentional rather than a blank glitch. */
const NO_VALUE_PLACEHOLDER = '--';

/**
 * Green for a positive change, red for negative, neutral when the value is exactly zero OR too small
 * to show as anything but "0.00" at `ASSET_LEADERBOARD_PERCENT_DISPLAY_INCREMENT` — otherwise a tiny
 * change (e.g. `0.004%`) would render as "0.00%" yet still show sign-colored.
 */
function percentChangeVariant(value: number) {
  return isFlatAtIncrement(value, ASSET_LEADERBOARD_PERCENT_DISPLAY_INCREMENT) ? undefined : numberSignVariant(value);
}

interface AssetLeaderboardValueCellProps extends ICellRendererParams<AssetLeaderboardEntity, number | null> {
  currency?: string;
  smartTruncate?: SmartTruncateEnum;
  smartTruncateThreshold?: number;
  /**
   * Price column only: prefix the value with a green/red tick-direction icon reflecting the last live
   * price move (`AssetLeaderboardEntity.priceTrend`), mirroring the Watchlist ref-rate ticker. Left off
   * for the static REST columns (Market Cap, Volume), which never tick intraday.
   */
  showTrend?: boolean;
}

/**
 * Plain formatted USD/number value (Price, Market Cap, Volume), or the shared "no data" placeholder
 * when null — kyoko's `type: 'number'` column renders null as an empty cell, so these columns use a
 * `type: 'custom'` cell instead, with `currency`/`smartTruncate` passed via `cellRendererParams`.
 * When `showTrend` is set (Price), a directional up/down icon precedes the value on a live tick.
 */
export function AssetLeaderboardValueCell({
  value,
  data,
  currency,
  smartTruncate,
  smartTruncateThreshold,
  showTrend,
}: AssetLeaderboardValueCellProps) {
  if (value == null) {
    return (
      <Text as="span" color="colorTextSubtle">
        {NO_VALUE_PLACEHOLDER}
      </Text>
    );
  }
  const formattedValue = (
    <InlineFormattedNumber
      number={value}
      currency={currency}
      smartTruncate={smartTruncate}
      smartTruncateThreshold={smartTruncateThreshold}
    />
  );
  if (!showTrend) {
    return formattedValue;
  }
  const trend = data?.priceTrend ?? 0;
  return (
    <HStack w="100%" justifyContent="flex-end" gap="spacing4">
      {trend !== 0 && (
        <Icon
          icon={trend > 0 ? IconName.TrendUp : IconName.TrendDown}
          color={trend > 0 ? 'green.lighten' : 'red.lighten'}
          size="fontSizeSmall"
        />
      )}
      {formattedValue}
    </HStack>
  );
}

/**
 * Plain signed, sign-colored percent change (e.g. `+4.87%` / `-1.20%`), 2 dp — used for the always-visible
 * 1h %/24h %/7d % columns, which show only the number, no sparkline.
 */
export function AssetLeaderboardPercentCell({ value }: ICellRendererParams<AssetLeaderboardEntity, number | null>) {
  if (value == null) {
    return (
      <Text as="span" color="colorTextSubtle">
        {NO_VALUE_PLACEHOLDER}
      </Text>
    );
  }
  return (
    <InlineFormattedNumber
      number={value}
      increment={ASSET_LEADERBOARD_PERCENT_DISPLAY_INCREMENT}
      currency="%"
      showSign
      variant={percentChangeVariant(value)}
    />
  );
}

/**
 * Signed, sign-colored 7-day percent change (e.g. `+4.87%` / `-1.20%`), 2 dp, with a sparkline of the
 * asset's recent daily price trend beneath it — same layout convention as Watchlist's price cell. Used
 * only by the "7d Price Change" column.
 */
export function AssetLeaderboardChangeCell({
  value,
  data,
}: ICellRendererParams<AssetLeaderboardEntity, number | null>) {
  if (value == null) {
    return (
      <Text as="span" color="colorTextSubtle">
        {NO_VALUE_PLACEHOLDER}
      </Text>
    );
  }
  const variant = percentChangeVariant(value);
  const priceHistory = data?.priceHistory ?? [];
  const showSparkline = priceHistory.length > 1;

  return (
    <VStack h="100%" w="100%" justifyContent="center" alignItems="center" gap="spacing2" py="spacing4">
      <InlineFormattedNumber
        number={value}
        increment={ASSET_LEADERBOARD_PERCENT_DISPLAY_INCREMENT}
        currency="%"
        showSign
        variant={variant}
      />
      {showSparkline && (
        <Box h="16px" w="100%">
          <Sparkline h="100%" w="100%" sparklineFill="gradient" data={priceHistory} variant={variant} />
        </Box>
      )}
    </VStack>
  );
}
